const ERASMUS_CACHE_PREFIX = "fpep_cms_erasmus_cache_v2";
const DEFAULT_CACHE_TTL_MS = 12 * 60 * 60 * 1000;

const parsePositiveIntegerEnv = (value, fallback) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const cacheTtlMs = parsePositiveIntegerEnv(
  process.env.REACT_APP_CMS_FALLBACK_CACHE_TTL_MS,
  DEFAULT_CACHE_TTL_MS,
);

function canUseStorage() {
  try {
    return typeof window !== "undefined" && Boolean(window.localStorage);
  } catch (_error) {
    return false;
  }
}

function getCacheKey(key) {
  return `${ERASMUS_CACHE_PREFIX}:${key}`;
}

function normalizeCacheLocale(locale = "me") {
  return String(locale || "me").toLowerCase().startsWith("en") ? "en" : "me";
}

function localizeCacheKey(key, locale = "me") {
  return `locale:${normalizeCacheLocale(locale)}:${key}`;
}

function readCache(key) {
  if (!canUseStorage()) return null;

  try {
    const raw = window.localStorage.getItem(getCacheKey(key));
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const createdAt = Number(parsed?.createdAt || 0);
    const ttl = Number(parsed?.ttlMs || cacheTtlMs);

    if (!parsed || !createdAt || Date.now() - createdAt > ttl) {
      window.localStorage.removeItem(getCacheKey(key));
      return null;
    }

    return parsed.payload || null;
  } catch (_error) {
    return null;
  }
}

function writeCache(key, payload) {
  if (!canUseStorage() || payload === undefined || payload === null) return;

  try {
    window.localStorage.setItem(
      getCacheKey(key),
      JSON.stringify({
        createdAt: Date.now(),
        ttlMs: cacheTtlMs,
        payload,
      }),
    );
  } catch (_error) {
    // localStorage may fail in private mode/quota errors. Public site must continue with local fallback.
  }
}

export function cacheCmsErasmusCalls(calls = [], locale = "me") {
  writeCache(localizeCacheKey("calls", locale), calls);
}

export function getCachedCmsErasmusCalls(locale = "me") {
  return readCache(localizeCacheKey("calls", locale)) || [];
}

export function cacheCmsErasmusDetail(slug, call, locale = "me") {
  if (!slug || !call) return;
  writeCache(localizeCacheKey(`call:${slug}`, locale), call);
}

export function getCachedCmsErasmusDetail(slug, locale = "me") {
  if (!slug) return null;
  return readCache(localizeCacheKey(`call:${slug}`, locale));
}

export function createErasmusFallbackNotice({ source = "local", reason = "unavailable" } = {}) {
  if (source === "cache") {
    return "CMS trenutno nije dostupan. Prikazani su posljednji sačuvani Erasmus konkursi.";
  }

  if (reason === "empty") {
    return "CMS trenutno nema objavljenih Erasmus konkursa. Prikazani su postojeći lokalni konkursi.";
  }

  return "CMS trenutno nije dostupan. Prikazani su postojeći lokalni Erasmus konkursi.";
}

export function getErasmusFallbackBadge(source = "local") {
  if (source === "cache") return "Cache";
  if (source === "local") return "Lokalna rezerva";
  return "CMS";
}

export function shouldSuppressErasmusFallbackNotice(error) {
  return error?.code === "CMS_DISABLED";
}
